import React, { useState, useEffect, useRef, useCallback } from "react";

const POLL_INTERVAL = 30000;

interface NotificationItem {
  notification_id: string;
  title?: string;
  message?: string;
  link?: string;
  read?: number | boolean;
  created_at?: string;
  [key: string]: unknown;
}

interface NotificationBellProps {
  patientId?: string;
}

function timeAgo(isoDate?: string): string {
  const diff = Date.now() - new Date(isoDate as string).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default function NotificationBell({ patientId }: NotificationBellProps) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const fetchCount = useCallback(async () => {
    if (!patientId) return;
    try {
      const res = await fetch(`/api/notifications/patient/${encodeURIComponent(patientId)}/count`);
      if (res.ok) {
        const data = await res.json();
        setUnreadCount(data.unread_count || 0);
      }
    } catch { /* silent */ }
  }, [patientId]);

  const fetchNotifications = useCallback(async () => {
    if (!patientId) return;
    try {
      const res = await fetch(`/api/notifications/patient/${encodeURIComponent(patientId)}?limit=20`);
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread_count || 0);
      }
    } catch { /* silent */ }
  }, [patientId]);

  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchCount]);

  useEffect(() => {
    if (isOpen) fetchNotifications();
  }, [isOpen, fetchNotifications]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    if (isOpen) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isOpen]);

  const handleNotificationClick = async (notif: NotificationItem) => {
    if (!notif.read) {
      try {
        await fetch(`/api/notifications/${notif.notification_id}/read`, { method: "PATCH" });
        setNotifications((prev) =>
          prev.map((n) => n.notification_id === notif.notification_id ? { ...n, read: 1 } : n)
        );
        setUnreadCount((c) => Math.max(0, c - 1));
      } catch { /* silent */ }
    }
    if (notif.link) {
      setIsOpen(false);
      window.location.href = notif.link;
    }
  };

  const handleMarkAllRead = async () => {
    if (!patientId) return;
    try {
      await fetch(`/api/notifications/patient/${encodeURIComponent(patientId)}/read-all`, { method: "POST" });
      setNotifications((prev) => prev.map((n) => ({ ...n, read: 1 })));
      setUnreadCount(0);
    } catch { /* silent */ }
  };

  return (
    <div style={{ position: "relative" }} ref={panelRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: 8,
          position: "relative",
          display: "flex",
          alignItems: "center",
        }}
        title="Notifications"
      >
        {/* Bell SVG */}
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unreadCount > 0 && (
          <span style={{
            position: "absolute",
            top: 2,
            right: 2,
            minWidth: 16,
            height: 16,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 10,
            fontWeight: 700,
            color: "white",
            backgroundColor: "#c25450",
            borderRadius: 8,
            padding: "0 4px",
          }}>
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div style={{
          position: "absolute",
          right: 0,
          top: "100%",
          marginTop: 8,
          width: 320,
          backgroundColor: "white",
          borderRadius: 12,
          boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
          border: "1px solid #e0e0e0",
          zIndex: 100,
          maxHeight: 420,
          display: "flex",
          flexDirection: "column",
          color: "#333",
        }}>
          {/* Header */}
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 16px",
            borderBottom: "1px solid #eee",
          }}>
            <span style={{ fontWeight: 600, fontSize: 14 }}>Notifications</span>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                style={{
                  background: "none",
                  border: "none",
                  color: "#182a33",
                  fontSize: 12,
                  cursor: "pointer",
                  fontWeight: 500,
                }}
              >
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div style={{ overflowY: "auto", flex: 1 }}>
            {notifications.length === 0 ? (
              <div style={{ padding: "32px 16px", textAlign: "center", color: "#999", fontSize: 14 }}>
                No notifications yet
              </div>
            ) : (
              notifications.map((notif) => (
                <button
                  key={notif.notification_id}
                  onClick={() => handleNotificationClick(notif)}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: "12px 16px",
                    borderBottom: "1px solid #f5f5f5",
                    background: !notif.read ? "rgba(42,77,105,0.05)" : "transparent",
                    border: "none",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                  }}
                >
                  {!notif.read && (
                    <span style={{
                      marginTop: 6,
                      width: 8,
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: "#182a33",
                      flexShrink: 0,
                    }} />
                  )}
                  <div style={{ marginLeft: notif.read ? 18 : 0 }}>
                    <div style={{
                      fontSize: 13,
                      fontWeight: !notif.read ? 600 : 400,
                      color: "#333",
                    }}>
                      {notif.title}
                    </div>
                    {notif.message && (
                      <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
                        {notif.message}
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: "#bbb", marginTop: 4 }}>
                      {timeAgo(notif.created_at)}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
